/**
 * Terminal-state resolution for a single position (repetition excluded — that
 * needs game history and lives in the repetition engine).
 *
 * Two xiangqi-specific points that western chess code routinely gets wrong:
 *   - Stalemate (困斃) is a LOSS for the side to move, not a draw.
 *   - The no-capture draw is the 60-move rule = 120 plies, not chess's 50.
 */

import { inCheck } from "./attacks.js";
import { legalMoves } from "./movegen.js";
import { Position } from "./position.js";
import { Color, opponent } from "./types.js";

export enum GameResult {
  Ongoing = "ongoing",
  RedWins = "red-wins",
  BlackWins = "black-wins",
  Draw = "draw",
}

export const NATURAL_DRAW_PLIES = 120; // 60 full moves without a capture

function winFor(color: Color): GameResult {
  return color === Color.Red ? GameResult.RedWins : GameResult.BlackWins;
}

/** True if the side to move has no legal moves (checkmate or stalemate — both losses). */
export function hasNoLegalMoves(pos: Position): boolean {
  return legalMoves(pos, pos.sideToMove).length === 0;
}

export function isCheckmate(pos: Position): boolean {
  return inCheck(pos, pos.sideToMove) && hasNoLegalMoves(pos);
}

export function isStalemate(pos: Position): boolean {
  return !inCheck(pos, pos.sideToMove) && hasNoLegalMoves(pos);
}

/**
 * Resolve the result of a position considering only what is knowable without
 * move history: mate, stalemate (a loss), and the natural no-capture draw.
 *
 * Repetition outcomes (perpetual check / chase) are decided by the repetition
 * engine, which owns the game's move history.
 */
export function resolveStatus(pos: Position): GameResult {
  if (hasNoLegalMoves(pos)) {
    // No moves — the side to move loses, whether checkmated (將死) or stalemated (困斃).
    return winFor(opponent(pos.sideToMove));
  }
  if (pos.halfmoveClock >= NATURAL_DRAW_PLIES) {
    return GameResult.Draw;
  }
  return GameResult.Ongoing;
}
